import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, UserCog, Ban, Mail, Copy, Trash2, KeyRound, Pencil } from 'lucide-react';
import type { Role, User } from '@conversia/shared-types';
import * as usersApi from '@/api/users.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageHeader } from '@/components/common/PageHeader';
import { roleLabel } from '@/lib/badges';
import { useAuthStore } from '@/store/auth.store';
import { useToast } from '@/hooks/useToast';

const roleOptions = [
  { value: 'ADMIN', label: 'Administrador' },
  { value: 'AGENT', label: 'Agente' },
  { value: 'VIEWER', label: 'Lector' },
  { value: 'TECNICO', label: 'Técnico' },
];

/**
 * Editar usuario (rediseño /users): nombre y EMAIL — para corregir typos
 * (gmai.com) sin recrear la cuenta. La unicidad la valida el backend.
 */
function EditarUsuarioModal({ user, onClose }: { user: User | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Pre-carga al abrir (user pasa de null → usuario).
  useEffect(() => {
    if (!user) return;
    setNombre(user.name ?? '');
    setEmail(user.email);
    setError(null);
  }, [user]);

  const save = useMutation({
    mutationFn: () =>
      usersApi.updateUser(user!.id, {
        ...(nombre.trim() && nombre.trim() !== (user!.name ?? '') ? { name: nombre.trim() } : {}),
        ...(email.trim().toLowerCase() !== user!.email ? { email: email.trim() } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('Usuario actualizado');
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'No se pudo guardar el usuario'),
  });

  function submit() {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Escribe un email válido');
      return;
    }
    setError(null);
    save.mutate();
  }

  return (
    <Modal
      open={Boolean(user)}
      onClose={onClose}
      title="Editar usuario"
      size="sm"
      description="Corrige el nombre o el email de acceso. El email debe ser único."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} loading={save.isPending}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} />
        <Input
          label="Email de acceso"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

const inviteSchema = z.object({
  email: z.string().min(1, 'El email es obligatorio').email('Email inválido'),
  role: z.enum(['ADMIN', 'AGENT', 'VIEWER', 'TECNICO']),
});
type InviteValues = z.infer<typeof inviteSchema>;

function InviteModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm<InviteValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', role: 'AGENT' },
  });
  const selectedRole = watch('role');

  const invite = useMutation({
    mutationFn: (values: InviteValues) => usersApi.invite(values),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invitations'] });
      // Sin decir mentiras: el correo solo sale si el servidor lo tiene
      // configurado; el enlace copiable de abajo funciona SIEMPRE.
      toast.success('Invitación creada — copie el enlace y mándelo por WhatsApp');
      reset();
      onClose();
    },
    onError: (err) =>
      setError('root', { message: err instanceof ApiError ? err.message : 'No se pudo invitar' }),
  });

  const onSubmit = handleSubmit((values) => invite.mutate(values));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invitar usuario"
      description="Se creará una invitación con un enlace de registro."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} loading={invite.isPending}>
            Enviar invitación
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Input label="Email" type="email" placeholder="persona@empresa.com" error={errors.email?.message} {...register('email')} />
        <Select label="Rol" options={roleOptions} {...register('role')} />
        {selectedRole === 'TECNICO' && (
          <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            La cuenta se crea cuando el técnico acepte la invitación. Falta UN paso después:
            vincularla en <span className="font-medium">Empleados → editar su ficha → «Cuenta de
            acceso»</span>. Sin ese vínculo, al entrar verá «Sin perfil de técnico».
          </p>
        )}
        {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}
      </form>
    </Modal>
  );
}

export default function Users() {
  const qc = useQueryClient();
  const toast = useToast();
  const meId = useAuthStore((s) => s.user?.id);
  const [inviteOpen, setInviteOpen] = useState(false);

  const { data: users, isLoading } = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => usersApi.listUsers({ take: 100 }),
  });
  // La tienda de cada persona y si recibe chats: el reparto da los chats de cada canal a su tienda.
  const { data: equipo } = useQuery({ queryKey: ['users', 'equipo'], queryFn: usersApi.equipoDelNegocio });
  const miembroDe = new Map((equipo?.miembros ?? []).map((m) => [m.userId, m]));
  const ajustar = useMutation({
    mutationFn: ({ id, cambios }: { id: string; cambios: { ubicacionId?: string | null; recibeChats?: boolean } }) =>
      usersApi.ajustarMiembro(id, cambios),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users', 'equipo'] });
      toast.success('Guardado');
    },
    onError: () => toast.error('No se pudo guardar'),
  });

  const { data: invitations } = useQuery({
    queryKey: ['invitations'],
    queryFn: () => usersApi.listInvitations(),
  });

  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => usersApi.updateRole(id, role),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('Rol actualizado');
    },
    onError: () => toast.error('No se pudo cambiar el rol'),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => usersApi.deactivate(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      toast.info('Usuario desactivado');
    },
    onError: () => toast.error('No se pudo desactivar'),
  });

  const reactivate = useMutation({
    mutationFn: (id: string) => usersApi.reactivate(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      toast.success('Usuario reactivado');
    },
    onError: () => toast.error('No se pudo reactivar'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => usersApi.revokeInvitation(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invitations'] });
      toast.info('Invitación revocada');
    },
    onError: () => toast.error('No se pudo revocar la invitación'),
  });

  // Reset por admin: la clave temporal se muestra UNA vez en un modal.
  const [tempPass, setTempPass] = useState<{ email: string; password: string } | null>(null);
  // Editar nombre/email (rediseño /users).
  const [editar, setEditar] = useState<User | null>(null);
  const resetPass = useMutation({
    mutationFn: async ({ id, email }: { id: string; email: string }) => ({
      email,
      result: await usersApi.adminResetPassword(id),
    }),
    onSuccess: ({ email, result }) => setTempPass({ email, password: result.tempPassword }),
    onError: () => toast.error('No se pudo restablecer la clave'),
  });

  function copyInviteLink(token: string) {
    const link = `${window.location.origin}/invite/${token}`;
    void navigator.clipboard
      .writeText(link)
      .then(() => toast.success('Enlace copiado — mándelo por WhatsApp'))
      .catch(() => toast.error(`No se pudo copiar; el enlace es: ${link}`));
  }

  const items = users?.items ?? [];

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Usuarios"
        helpKey="users"
        description="Gestiona el equipo, sus roles e invitaciones."
        action={
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4" /> Invitar usuario
          </Button>
        }
      />

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={UserCog}
              title="Sin usuarios"
              description="Invite a su equipo con el botón de arriba; el enlace se puede mandar por WhatsApp."
              className="m-5"
            />
          ) : (
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {items.map((u) => (
                <li key={u.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <Avatar name={u.name ?? u.email} size="md" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                      {u.name ?? '—'}{' '}
                      {u.id === meId && <span className="text-xs text-slate-400">(tú)</span>}
                    </p>
                    <p className="truncate text-xs text-slate-400">{u.email}</p>
                  </div>
                  {!u.isActive && <Badge variant="danger">Inactivo</Badge>}
                  {/* Solo las vendedoras entran en el reparto: la dueña y la administración ven todo. */}
                  {miembroDe.get(u.id)?.atiende && (
                    <>
                      <Select
                        className="w-44"
                        aria-label={`Tienda de ${u.name ?? u.email}`}
                        value={miembroDe.get(u.id)!.ubicacionId ?? ''}
                        options={[
                          { value: '', label: 'Sin tienda' },
                          ...(equipo?.tiendas ?? []).map((t) => ({ value: t.id, label: t.nombre })),
                        ]}
                        disabled={ajustar.isPending && ajustar.variables?.id === u.id}
                        onChange={(e) => ajustar.mutate({ id: u.id, cambios: { ubicacionId: e.target.value || null } })}
                      />
                      <label className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                        <input
                          type="checkbox"
                          checked={miembroDe.get(u.id)!.recibeChats}
                          disabled={ajustar.isPending && ajustar.variables?.id === u.id}
                          onChange={(e) => ajustar.mutate({ id: u.id, cambios: { recibeChats: e.target.checked } })}
                        />
                        Recibe chats
                      </label>
                    </>
                  )}
                  <Select
                    className="w-40"
                    value={u.role}
                    options={roleOptions}
                    disabled={u.id === meId || changeRole.isPending}
                    onChange={(e) => changeRole.mutate({ id: u.id, role: e.target.value as Role })}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Editar nombre y email"
                    onClick={() => setEditar(u)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Restablecer clave (genera una temporal)"
                    disabled={u.id === meId}
                    onClick={() => resetPass.mutate({ id: u.id, email: u.email })}
                    loading={resetPass.isPending && resetPass.variables?.id === u.id}
                  >
                    <KeyRound className="h-4 w-4" />
                  </Button>
                  {u.isActive ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Desactivar usuario"
                      disabled={u.id === meId}
                      onClick={() => deactivate.mutate(u.id)}
                      loading={deactivate.isPending && deactivate.variables === u.id}
                    >
                      <Ban className="h-4 w-4 text-danger" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => reactivate.mutate(u.id)}
                      loading={reactivate.isPending && reactivate.variables === u.id}
                    >
                      Reactivar
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {invitations && invitations.length > 0 && (
        <Card>
          <CardBody className="p-0">
            <div className="border-b border-surface-border px-5 py-3 dark:border-slate-800">
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Invitaciones pendientes</h2>
            </div>
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {invitations.map((inv) => (
                <li key={inv.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                    <Mail className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-slate-800 dark:text-slate-100">{inv.email}</p>
                  </div>
                  <Badge variant="warning">{roleLabel[inv.role]}</Badge>
                  <Button variant="outline" size="sm" onClick={() => copyInviteLink(inv.token)}>
                    <Copy className="mr-1 h-3.5 w-3.5" /> Copiar enlace
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Revocar invitación"
                    onClick={() => revoke.mutate(inv.id)}
                    loading={revoke.isPending && revoke.variables === inv.id}
                  >
                    <Trash2 className="h-4 w-4 text-danger" />
                  </Button>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} />

      <EditarUsuarioModal user={editar} onClose={() => setEditar(null)} />

      {/* Clave temporal generada: se muestra UNA vez para pasarla por WhatsApp. */}
      <Modal
        open={tempPass !== null}
        onClose={() => setTempPass(null)}
        title="Clave temporal generada"
        description={`Pásesela a ${tempPass?.email ?? ''} por WhatsApp. No se vuelve a mostrar.`}
        footer={
          <Button onClick={() => setTempPass(null)}>Listo, la copié</Button>
        }
      >
        <div className="space-y-3">
          <p className="rounded-md bg-slate-100 px-4 py-3 text-center font-mono text-lg font-semibold tracking-wide text-slate-900 dark:bg-slate-800 dark:text-slate-100">
            {tempPass?.password}
          </p>
          <p className="text-xs text-slate-400">
            Las sesiones anteriores de ese usuario quedaron cerradas. Recomiéndele cambiar
            la clave al entrar.
          </p>
        </div>
      </Modal>
    </div>
  );
}
